/**
 * VersionChangelogDialog —— 后台版本迭代日志弹窗
 *
 * 从 /changelog.json 读取由构建期生成的版本记录，
 * 展示从项目启动到当前的所有版本变更，解决 Admin 左下角「v1.0.0」只是装饰的问题。
 */

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Link,
  Alert,
  CircularProgress,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import { useEffect, useState } from 'react'

interface ChangelogSection {
  title: string
  items: string[]
}

interface ChangelogVersion {
  version: string
  date: string
  sections: ChangelogSection[]
}

interface VersionChangelogDialogProps {
  open: boolean
  currentVersion: string
  onClose: () => void
}

export function VersionChangelogDialog({ open, currentVersion, onClose }: VersionChangelogDialogProps) {
  const [versions, setVersions] = useState<ChangelogVersion[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setLoading(true)
    // 使用 Vite 构建基路径（方案C 改为 /），避免根路径部署时 404 或命中错误页面。
    fetch(import.meta.env.BASE_URL + 'changelog.json', { cache: 'no-store' })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        return res.json()
      })
      .then((data: ChangelogVersion[]) => {
        setVersions(Array.isArray(data) ? data : [])
        setError(null)
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : '加载失败')
      })
      .finally(() => setLoading(false))
  }, [open])

  const firstVersion = versions[0]?.version
  const isCurrentLatest = currentVersion === firstVersion

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Typography variant="h6" component="span">
            版本迭代记录
          </Typography>
          <Chip
            size="small"
            label={`当前 ${currentVersion}`}
            color={isCurrentLatest ? 'success' : 'warning'}
          />
          {!isCurrentLatest && firstVersion && (
            <Chip size="small" label={`最新 ${firstVersion}`} color="info" />
          )}
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        )}

        {error && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            无法加载版本日志（{error}），请稍后重试或联系开发。
          </Alert>
        )}

        {!loading && !error && versions.length === 0 && (
          <Alert severity="info">暂无版本记录。</Alert>
        )}

        {versions.map((v, idx) => {
          const isCurrent = v.version === currentVersion
          return (
            <Accordion key={v.version} defaultExpanded={idx === 0} disableGutters>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%', pr: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, minWidth: 80 }}>
                    {v.version}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {v.date}
                  </Typography>
                  {isCurrent && (
                    <Chip
                      size="small"
                      label="当前版本"
                      color="primary"
                      sx={{ ml: 'auto', height: 22, fontSize: '11px' }}
                    />
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 2, pb: 2 }}>
                {v.sections.map((sec) => (
                  <Box key={sec.title} sx={{ mb: 1.5 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 600,
                        color: sectionColor(sec.title),
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                      }}
                    >
                      {sec.title}
                    </Typography>
                    <Box component="ul" sx={{ m: 0, pl: 2 }}>
                      {sec.items.map((item, i) => (
                        <Typography
                          key={i}
                          component="li"
                          variant="body2"
                          sx={{ color: 'text.primary', mb: 0.5, lineHeight: 1.6 }}
                        >
                          {item}
                        </Typography>
                      ))}
                    </Box>
                  </Box>
                ))}
              </AccordionDetails>
            </Accordion>
          )
        })}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button
          size="small"
          startIcon={<OpenInNewIcon />}
          href="/CHANGELOG_USER.md"
          target="_blank"
          rel="noopener noreferrer"
        >
          查看完整记录
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose} variant="contained" size="small">
          知道了
        </Button>
      </DialogActions>
    </Dialog>
  )
}

function sectionColor(title: string): string {
  if (title.includes('新增')) return '#2e7d32'
  if (title.includes('优化')) return '#ed6c02'
  if (title.includes('修复')) return '#d32f2f'
  if (title.includes('关联')) return '#757575'
  return '#757575'
}
